import React, { useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { Terminal, Crosshair } from "lucide-react";
export default function EngineeringPrecisionForIndustrialProgress() {
  const sectionRef = useRef(null);
  useEffect(() => {
    const loadScript = src => {
      return new Promise(resolve => {
        if (document.querySelector(`script[src="${src}"]`)) {
          resolve();
          return;
        }
        const script = document.createElement("script");
        script.src = src;
        script.onload = resolve;
        document.head.appendChild(script);
      });
    };
    Promise.all([loadScript("https://cdnjs.cloudflare.com/ajax/libs/gsap/3.12.2/gsap.min.js"), loadScript("https://cdnjs.cloudflare.com/ajax/libs/gsap/3.12.2/ScrollTrigger.min.js")]).then(() => {
      const gsap = window.gsap;
      if (!gsap) return;
      const ScrollTrigger = window.gsap.plugins && window.gsap.plugins.scrollTrigger ? window.gsap.plugins.scrollTrigger : window.ScrollTrigger;
      if (ScrollTrigger) {
        gsap.registerPlugin(ScrollTrigger);
      }
      const ctx = gsap.context(() => {
        const tl = gsap.timeline({
          scrollTrigger: {
            trigger: sectionRef.current,
            start: "top top",
            end: "+=4500",
            pin: true,
            scrub: 1,
            markers: false
          }
        });
        tl.to(".hero-hud", {
          opacity: 0,
          y: -50,
          duration: 1
        });
        tl.fromTo(".hero-text-2", {
          y: 50,
          opacity: 0,
          scale: 0.95
        }, {
          y: 0,
          opacity: 1,
          scale: 1,
          duration: 1.5,
          ease: "power2.out"
        });
        tl.to(".hero-text-2", {
          y: 0,
          duration: 1
        });
        tl.to(".hero-text-2", {
          opacity: 0,
          y: -50,
          duration: 1
        });
        tl.fromTo(".hero-text-3", {
          y: 50,
          opacity: 0
        }, {
          y: 0,
          opacity: 1,
          duration: 1.5,
          ease: "power2.out"
        });
        tl.to(".hero-text-3", {
          y: 0,
          duration: 2
        });
        tl.to(".hero-text-3", {
          opacity: 0,
          y: -50,
          duration: 1
        });
        tl.fromTo(".service-cards", {
          y: 100,
          opacity: 0
        }, {
          y: 0,
          opacity: 1,
          duration: 2,
          ease: "power2.out"
        });
        tl.to(".service-cards", {
          y: 0,
          duration: 2
        });
      }, sectionRef);
      return () => ctx.revert();
    });
  }, []);
  return <section ref={sectionRef} data-section-id="224938" id="օրբիտալ-հրամանատարություն" className="text-foreground relative h-screen w-full overflow-hidden flex flex-col justify-center dark bg-slate-900">
      <style dangerouslySetInnerHTML={{
      __html: `
          @keyframes border-pulse {
            0% { box-shadow: 0 0 0 0 rgba(var(--color-primary-rgb), 0.4); }
            70% { box-shadow: 0 0 0 15px rgba(var(--color-primary-rgb), 0); }
            100% { box-shadow: 0 0 0 0 rgba(var(--color-primary-rgb), 0); }
          }
          .animate-border-pulse {
            animation: border-pulse 2s infinite;
          }
        `
    }} />

      {}
      <div className="absolute inset-0 z-0">
        <div className="absolute inset-0 bg-[url('https://summary-monarch-dev.10web.cloud/wp-content/uploads/2026/06/orbital_earth_view.jpg')] bg-cover bg-center contrast-[1.1] brightness-[0.4] h-full opacity-60" />
      </div>

      {}
      <div className="hero-hud absolute inset-0 z-10 flex items-center justify-center px-4 md:px-8">
        <div className="container mx-auto grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
          <div className="hidden lg:flex flex-col gap-6 font-default text-[clamp(10px,2vw,12px)] text-muted-foreground border-l border-primary/30 pl-4 py-4 backdrop-blur-md bg-background/20 w-[200px] lg:col-span-3">
            <div className="flex items-center gap-2 text-primary">
              <Terminal className="size-4" />
              <span>ORBIT.INIT</span>
            </div>
            <div className="space-y-1">
              <p className="uppercase tracking-widest text-foreground/60">Coordinates</p>
              <p>LAT: 40.1772° N</p>
              <p>LONG: 44.5035° E</p>
              <p>ALT: 408.00 km</p>
            </div>
            <div className="space-y-1">
              <p className="uppercase tracking-widest text-foreground/60">Data Stream</p>
              <p>BAND: X-BAND</p>
              <p>ENCRYPT: AES-256</p>
            </div>
            <div className="space-y-1">
              <p className="uppercase tracking-widest text-foreground/60">System</p>
              <p className="text-primary animate-pulse">● CONNECTED</p>
            </div>
          </div>
          <div className="lg:col-span-9 flex flex-col items-start">
            <h1 className="font-default font-bold tracking-tighter text-transparent bg-clip-text bg-gradient-to-b from-white via-white to-neutral-400 mb-4 uppercase text-[clamp(1.75rem,6vw,4rem)] leading-[1.1]">
              Դիտում Ենք Տիեզերքից
            </h1>
            <div className="flex items-center space-x-4 text-green-400 font-mono text-sm">
              <span>ORBIT.INIT</span>
              <span className="inline-block h-2 w-2 rounded-lg bg-green-500 animate-pulse"></span>
              <span>CONNECTED</span>
            </div>
          </div>
        </div>
      </div>

      {}
      <div className="hero-text-2 absolute inset-0 flex flex-col items-center justify-center z-20 text-center opacity-0 pointer-events-none px-4">
        <h2 className="text-4xl md:text-7xl font-bold font-default tracking-widest text-white uppercase">
          Գործում Երկրի Վրա
        </h2>
      </div>

      {}
      <div className="hero-text-3 absolute inset-0 flex flex-col justify-center z-30 px-6 md:px-20 max-w-6xl mx-auto opacity-0 pointer-events-none">
        <div className="border-l-2 border-primary pl-6 md:pl-8 py-2 max-w-4xl">
          <p className="text-[clamp(14px,2.5vw,20px)] text-muted-foreground font-default font-light leading-relaxed">
            «Գեոկոսմոս» ընկերությունն հանդիսանում է արբանյակային նկարահանման առաջատարը շուկայում։ Պատկերները ստացվում են սեփական, ինչպես նաև ոլորտի միջազգային առաջատար գործընկերներից։ Ընկերությունը մասնագիտացած է նաև պատկերների ժամանակակից տեխնոլոգիաներով ֆոտոգրամետրիական մշակման, թվային քարտեզների ստեղծման և թարմացման, ինչպես նաև գեոդեզիական աշխատանքների վրա:
          </p>
        </div>
        <div className="mt-10 ml-6 md:ml-8 pointer-events-auto">
          <div className="relative group inline-block">
            <div className="absolute -inset-1 bg-primary/25 animate-ping opacity-20" />
            <div className="absolute inset-0 border border-primary animate-border-pulse pointer-events-none" />
            <Button asChild size="lg" className="relative rounded-md bg-background/80 backdrop-blur-md border border-primary/50 text-foreground hover:bg-primary hover:text-primary-foreground transition-all duration-300 h-[clamp(44px,10vw,64px)] px-[clamp(24px,5vw,40px)] text-[clamp(12px,3vw,18px)] font-default tracking-wider uppercase">
              <Link to="#">
                Իմանալ Ավելին
                <Crosshair className="ml-3 size-5 opacity-70 group-hover:rotate-90 transition-transform duration-500" />
              </Link>
            </Button>
          </div>
        </div>
      </div>

      {}
      <div className="service-cards absolute inset-0 flex items-center justify-center z-40 opacity-0 pointer-events-none">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 px-4 max-w-6xl w-full pointer-events-auto">
          <div className="p-8 bg-slate-900/80 backdrop-blur-md rounded-2xl border border-slate-600 text-left hover:border-primary transition-colors">
            <h3 className="text-xl font-semibold mb-2 text-white">ԱՐԲԱՆՅԱԿԱՅԻՆ ՊԱՏԿԵՐՆԵՐ</h3>
            <p className="text-slate-300 text-sm">Բարձր լուծաչափով օպտիկական և ռադարային տվյալներ</p>
          </div>
          <div className="p-8 bg-slate-900/80 backdrop-blur-md rounded-2xl border border-slate-600 text-left hover:border-primary transition-colors">
            <h3 className="text-xl font-semibold mb-2 text-white">ՖՈՏՈԳՐԱՄԵՏՐԻԱ</h3>
            <p className="text-slate-300 text-sm">Արբանյակային տվյալների ճշգրիտ մշակում</p>
          </div>
          <div className="p-8 bg-slate-900/80 backdrop-blur-md rounded-2xl border border-slate-600 text-left hover:border-primary transition-colors">
            <h3 className="text-xl font-semibold mb-2 text-white">3D ՄՈԴԵԼԱՎՈՐՈՒՄ</h3>
            <p className="text-slate-300 text-sm">Ռելիեֆի թվային մոդելներ (DEM/DSM)</p>
          </div>
        </div>
      </div>
    </section>;
}